import React, { memo, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';

import { AppContext } from '@prisjakt/ui-app-context';
import ProductRow from '@prisjakt/ui-product-row';
import ProductCard from '@prisjakt/ui-product-card';
import ProductListItem from '@prisjakt/ui-product-list-item';

import * as S from './styles';

const renderPropertyValue = (property, t) => {
  if (property.type === 'boolean') {
    if (property.prettyTable === null) return '-';
    return property.prettyTable === 'true'
      ? t('category', 'property.boolean-true')
      : t('category', 'property.boolean-false');
  }

  return property.prettyTable;
};

const Product = ({
  id,
  mode,
  link,
  name,
  image,
  price,
  rating,
  ratingCount,
  category,
  campaignBadge,
  brand,
  storesCount,
  storesCountText,
  properties,
  selectable,
  selected,
  onSelect,
  onDeselect,
  onClick,
}) => {
  const { t } = useContext(AppContext);
  const select = useCallback(() => onSelect(id, 'productCard'), [id, onSelect]);
  const deselect = useCallback(() => onDeselect(id, 'productCard'), [
    id,
    onDeselect,
  ]);

  switch (mode) {
    case 'row':
      return (
        <ProductRow
          link={link}
          name={name}
          image={image}
          price={price}
          rating={rating}
          ratingCount={ratingCount}
          subtitle={category && category.name}
          campaignBadge={campaignBadge}
          storesCount={storesCount}
          storesCountText={storesCountText}
          showDivider
          selectable={selectable}
          selected={selected}
          onSelect={select}
          onDeselect={deselect}
          onClick={onClick}
        />
      );
    case 'grid':
      return (
        <ProductCard
          link={link}
          name={name}
          image={image}
          price={price}
          rating={rating}
          ratingCount={ratingCount}
          category={category}
          campaignBadge={campaignBadge}
          brandLogo={
            brand && brand.featured && brand.logo ? (
              <img src={brand.logo} alt={brand.name} />
            ) : null
          }
          storesCount={storesCount}
          storesCountTranslation={storesCountText}
          selectable={selectable}
          selected={selected}
          onSelect={select}
          onDeselect={deselect}
          onClick={onClick}
        />
      );
    case 'table':
      return (
        <ProductListItem
          as="li"
          mode={mode}
          link={link}
          name={name}
          image={image}
          price={price}
          rating={rating}
          count={ratingCount}
          campaignBadge={campaignBadge}
          storesCount={storesCount}
          storesCountTranslation={storesCountText}
          properties={properties.map(property => (
            <S.TooltipTrigger key={property.id}>
              {renderPropertyValue(property, t)}
              {property.prettyTable !== null && (
                <S.Tooltip position="left">
                  {property.type === 'boolean'
                    ? `${property.name}: ${renderPropertyValue(property, t)}`
                    : property.prettyVerbose}
                </S.Tooltip>
              )}
            </S.TooltipTrigger>
          ))}
          smaller
          selectable={selectable}
          selected={selected}
          onSelect={select}
          onDeselect={deselect}
          onClick={onClick}
        />
      );
    default:
      return null;
  }
};

const { oneOf, number, shape, string, bool, node, func, arrayOf } = PropTypes;

Product.propTypes = {
  id: number.isRequired,
  mode: oneOf(['grid', 'row', 'table']).isRequired,
  link: string.isRequired,
  name: string.isRequired,
  image: node,
  rating: number,
  ratingCount: number,
  category: shape({
    name: string.isRequired,
    pathName: string,
  }),
  campaignBadge: node,
  brand: shape({
    name: string,
    logo: string,
    featured: bool,
  }),
  storesCount: number,
  storesCountText: string,
  properties: arrayOf(
    shape({
      id: string,
      type: string,
      name: string,
      prettyTable: string,
      prettyVerbose: string,
    })
  ),
  selectable: bool,
  selected: bool,
  onSelect: func,
  onDeselect: func,
  onClick: func,
};

Product.defaultProps = {
  image: null,
  rating: null,
  ratingCount: null,
  category: null,
  campaignBadge: null,
  brand: null,
  storesCount: null,
  storesCountText: null,
  properties: [],
  selectable: false,
  selected: false,
  onSelect: () => {},
  onDeselect: () => {},
  onClick: () => {},
};

export default memo(Product);
